import React, { SyntheticEvent, useEffect, useState } from "react";
import { Button, Form, FormField, FormGroup, Input } from "semantic-ui-react";

// Component
import TableIssuesTracking from "./TableIssuesTracking";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import CardTransferList from "react-lib/apps/USER/CardTransferList";

// Interface
import {
  BTN_ACTS,
  MasterOptions,
  PickedState,
  SetProp,
  ProjectListType,
  IssuesTrackingType,
} from "./sequence/ProjectList";

// Type
type PanelCreateProjectProps = {
  // func
  setProp: SetProp;
  onChange: (e: SyntheticEvent | null, data: Record<string, any>) => void;
  onCreate: () => void;
  onEdit: () => void;
  onActive: () => void;
  onInactive: () => void;
  onClear: () => void;
  // data
  data: ProjectListType;
  tableData: IssuesTrackingType[];
  masterOptions: MasterOptions;
} & Pick<PickedState, "buttonLoadCheck" | "errorMessage" | "successMessage">;

type RenderActionButtonProps = {
  setProp: SetProp;
  onCreate?: () => void;
  onEdit?: () => void;
  onActive?: () => void;
  onInactive?: () => void;
  onClear?: () => void;
  editable?: boolean;
  isActive?: boolean;
} & Pick<PickedState, "buttonLoadCheck" | "successMessage" | "errorMessage">;

/*================================ RENDERING ==============================*/

const RenderActionButton = (props: RenderActionButtonProps) => {
  const { editable, isActive } = props;

  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
      <ButtonLoadCheck
        // func
        setProp={props.setProp}
        onClick={editable ? props.onEdit : props.onCreate}
        // data
        paramKey={editable ? BTN_ACTS.EDIT : BTN_ACTS.CREATE}
        buttonLoadCheck={props.buttonLoadCheck?.[editable ? BTN_ACTS.EDIT : BTN_ACTS.CREATE]}
        // config
        name={editable ? "EDIT" : "CREATE"}
        title={editable ? "EDIT" : "CREATE"}
        color={editable ? "yellow" : "blue"}
        size="medium"
      />
      {editable && (
        <ButtonLoadCheck
          // func
          setProp={props.setProp}
          onClick={isActive ? props.onInactive : props.onActive}
          // data
          paramKey={BTN_ACTS.ARCHIVE}
          buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.ARCHIVE]}
          // config
          name={isActive ? "INACTIVE" : "ACTIVE"}
          title={isActive ? "INACTIVE" : "ACTIVE"}
          color={isActive ? "orange" : "green"}
          size="medium"
        />
      )}
      <Button
        onClick={props.onClear}
        children={editable ? "CLOSE" : "CLEAR"}
        color={editable ? "red" : "orange"}
        size="medium"
      />
    </div>
  );
};

/*================================ HANDLER ==============================*/

const isActiveProject = (project: ProjectListType) => {
  const activeField = project.custom_fields?.find((f) => f.name === "Active");

  return activeField?.value === "1"; // true = active, false = inactive
};


/*================================ MAIN COMPONENT ==============================*/

const PanelCreateProject = (props: PanelCreateProjectProps) => {
  const { data, tableData } = props;
  const editable = !!data.id;
  const isActive = isActiveProject(data);

  const getDivisionField = () => props.data?.custom_fields?.find((f) => f.name === "Division");

  const [selectedDivisions, setSelectedDivisions] = useState<string[]>(
    getDivisionField()?.value || []
  );

  useEffect(() => {
    setSelectedDivisions(getDivisionField()?.value || []);
  }, [props.data.custom_fields]);

  const handleSelectedDivision = (selected: (string | number)[]) => {
    const selectedAsString = selected.map(String);

    setSelectedDivisions(selectedAsString);

    props.onChange?.(null, { name: "Division", value: selectedAsString });
  };

  return (
    <div>
      <Form style={{ paddingLeft: "1rem", paddingTop: "1rem", paddingRight: "1rem" }}>
        <FormGroup>
          <FormField>
            <label style={{ fontSize: "18px", fontWeight: "bold" }}>Create New Project</label>
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField inline width={16}>
            <label>Project Name:</label>
            <Input
              style={{ width: "65%" }}
              value={data.name || ""}
              name="name"
              onChange={props.onChange}
            />
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField>
            <label>Related Division:</label>
          </FormField>
        </FormGroup>
        <FormGroup style={{ marginLeft: "2rem" }}>
          <FormField>
            <CardTransferList
              list={props.masterOptions?.division || []}
              textKey="text"
              valueKey="text"
              selected={selectedDivisions}
              onSelected={handleSelectedDivision}
            />
          </FormField>
        </FormGroup>
        {editable && tableData.length > 0 && (
          <>
            <FormGroup>
              <FormField>
                <label>Issues Tracking:</label>
              </FormField>
            </FormGroup>
            <FormGroup style={{ marginLeft: "2rem" }}>
              <FormField>
                <TableIssuesTracking data={tableData} />
              </FormField>
            </FormGroup>
          </>
        )}
        <FormGroup>
          <FormField inline width={16}>
            <RenderActionButton
              editable={editable}
              isActive={isActive}
              setProp={props.setProp}
              onClear={props.onClear}
              onCreate={props.onCreate}
              onEdit={props.onEdit}
              onActive={props.onActive}
              onInactive={props.onInactive}
              buttonLoadCheck={props.buttonLoadCheck}
            />
          </FormField>
        </FormGroup>
      </Form>
    </div>
  );
};

export default PanelCreateProject;
